import { useMemo, useCallback } from "react";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";

/**
 * Hook to validate that a node name is not duplicated in the workflow.
 * Excludes initializer-node and finish-node from comparison.
 *
 * @param currentNodeId - ID of the current node being edited
 * @returns Object with validation functions and state
 */
export const useNodeNameValidation = (currentNodeId: string | undefined) => {
    const { nodes } = useWorkflowStore();

    // Excluded nodes from validation (special system nodes)
    const excludedNodeTypes = useMemo(
        () => [NodeTypes.INITIALIZER, NodeTypes.END],
        [],
    );

    // Get all existing node names (excluding current node and excluded types)
    const existingNodeNames = useMemo(() => {
        if (!nodes || !Array.isArray(nodes)) {
            return [];
        }
        return nodes
            .filter((node) => {
                const nodeData = node.data as unknown as NodeData;
                // Exclude current node
                if (node.id === currentNodeId) return false;
                // Exclude special nodes (initializer and finish)
                const nodeType = nodeData?.type;
                if (nodeType && excludedNodeTypes.includes(nodeType as (typeof excludedNodeTypes)[number])) return false;
                return true;
            })
            .map((node) => {
                const nodeData = node.data as unknown as NodeData;
                return nodeData?.displayName?.toLowerCase() || "";
            })
            .filter((name) => name !== "");
    }, [nodes, currentNodeId, excludedNodeTypes]);

    /**
     * Checks if a name already exists in other workflow nodes
     * Comparison is case-insensitive
     */
    const isNameDuplicate = useCallback(
        (name: string): boolean => {
            if (!name || name.trim() === "") return false;
            const normalizedName = name.trim().toLowerCase();
            return existingNodeNames.includes(normalizedName);
        },
        [existingNodeNames],
    );

    /**
     * Gets the validation error message for the node name
     * Returns undefined if there is no error
     */
    const getNameValidationError = useCallback(
        (name: string): string | undefined => {
            if (!name || name.trim() === "") {
                return "El nombre del nodo es requerido";
            }
            if (isNameDuplicate(name)) {
                return "Ya existe un nodo con este nombre";
            }
            return undefined;
        },
        [isNameDuplicate],
    );

    /**
     * Checks if the name is valid (not empty and not duplicated)
     */
    const isNameValid = useCallback(
        (name: string): boolean => {
            return getNameValidationError(name) === undefined;
        },
        [getNameValidationError],
    );

    return {
        isNameDuplicate,
        getNameValidationError,
        isNameValid,
        existingNodeNames,
    };
};

export default useNodeNameValidation;
